/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import isEmpty from 'lodash/isEmpty';

import { useAccountType } from '../../hooks/accountHooks.js';
import { fetchPricingView } from '../../actions/pricingViewActions.js';

import {
    salesChannelAllowsGuestType,
    getAccountTypeSelector,
    tourHasExternalGuestTypes,
} from '../../utils/index.js';
import { FeesTab } from '../../components/FacilityPage/FeesTab.jsx';

export function FeesTabContainer() {
    const { facilityId } = useParams();
    const dispatch = useDispatch();
    const account = useSelector((state) => state?.auth?.account) || {};
    const facilityData = useSelector((state) => state.timedEntry.facility);
    const pricingViewData = useSelector((state) => state.timedEntry.pricingView);
    const { isCommAccount, commTourList, nonCommTourList } = useAccountType();

    const { guest_types: guestTypes, change_cancel_desc: changesAndCancellationsHtml } =
        facilityData;

    const { pricesByCategory, facility_id: reduxFacilityId, isLoading } = pricingViewData;

    const toursToCheck = useMemo(() => {
        let tours = nonCommTourList;

        if (isCommAccount) {
            tours = commTourList;
        }

        tours = tours?.filter((tour) => {
            return tourHasExternalGuestTypes(guestTypes, tour);
        });

        return tours;
    }, [commTourList, guestTypes, isCommAccount, nonCommTourList]);

    useEffect(() => {
        if (!reduxFacilityId || reduxFacilityId !== facilityId) {
            dispatch(fetchPricingView(facilityId));
        }
    }, [dispatch, reduxFacilityId, facilityId]);

    const filteredGuestTypes = guestTypes.filter((guestType) =>
        salesChannelAllowsGuestType(guestType)
    );

    const accountType = getAccountTypeSelector(account, facilityId);

    const salesCategory = accountType === 'COMM' ? 'ORGANIZATION' : 'INDIVIDUAL';

    // we attempt to get the ORGANIZATION specific fees and if they are not found
    let tourPrices = pricesByCategory?.[salesCategory] || [];

    if (isEmpty(tourPrices)) {
        tourPrices = pricesByCategory?.INDIVIDUAL || [];
    }

    const filteredTourPrices = tourPrices.filter((tourPrice) =>
        toursToCheck.find((tour) => tour.tour_id === tourPrice.tour_id)
    );

    return (
        <FeesTab
            isFeesLoading={isLoading}
            tourPrices={filteredTourPrices}
            guestTypes={filteredGuestTypes}
            changesAndCancellationsHtml={changesAndCancellationsHtml}
        />
    );
}
